import { ResultsGrid } from "@/components/molecules/modernise-with-ai/ResultsGrid";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@ncoder/ui";
import { CheckCircle, RefreshCw } from "lucide-react";

interface ModernisationResultsProps {
  results: {
    totalProcessed: number;
    successful: number;
    failed: number;
    performanceGain: number;
    codeReduction: number;
  };
  onReset: () => void;
}

export const ModernisationResults: React.FC<ModernisationResultsProps> = ({
  results,
  onReset,
}) => (
  <div className="mt-10">
    {/* Header */}
    <div className="flex items-center justify-between mb-8">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
            <CheckCircle className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Modernization Complete</h1>
            <p className="text-muted-foreground">
              Your org has been successfully modernized
            </p>
          </div>
        </div>
      </div>
      <Button onClick={onReset}>
        <RefreshCw className="h-4 w-4 mr-2" />
        Start New Modernization
      </Button>
    </div>

    {/* Stats */}
    <ResultsGrid results={results} />

    {/* Summary and Next Steps */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Modernization Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-950 rounded-lg">
              <span>Code Reduction</span>
              <Badge variant="secondary">{results.codeReduction}%</Badge>
            </div>
            <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <span>Performance Improvement</span>
              <Badge variant="secondary">{results.performanceGain}%</Badge>
            </div>
            <div className="flex justify-between items-center p-3 bg-purple-50 dark:bg-purple-950 rounded-lg">
              <span>Success Rate</span>
              <Badge variant="secondary">
                {Math.round(
                  (results.successful / results.totalProcessed) * 100
                )}
                %
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Next Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              "Deploy changes to production",
              "Run comprehensive testing",
              "Update documentation",
              "Train team on new components",
            ].map((step, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm">{step}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);
